/**
 * Chart system constants — UI behavior only.
 * Market-specific values (threshold, decay, floor, graduation) come from the API.
 */

import type { ChartInterval, ChartMode } from '@/lib/chart/types';

/** Default time bucket interval for chart data queries. */
export const DEFAULT_INTERVAL: ChartInterval = 'all';

/** Default chart tab (consensus vs live). */
export const DEFAULT_CHART_MODE: ChartMode = 'consensus';

/** Glow animation cycle duration in normal state (ms). */
export const GLOW_CYCLE_NORMAL_MS = 2000;

/** Glow animation cycle duration in race mode (ms). */
export const GLOW_CYCLE_RACE_MS = 800;

/** Duration of price-flash highlight effect (ms). */
export const FLASH_DURATION_MS = 500;

/** Number of historical data points to request on initial load. */
export const INITIAL_DATA_POINTS = 200;

/** Polling fallback interval when realtime is unavailable (ms). */
export const POLLING_FALLBACK_INTERVAL_MS = 30_000;

/** Percentage within threshold that activates race mode. */
export const RACE_MODE_THRESHOLD_PERCENT = 10;

/** Maximum rows in the trade feed panel. */
export const MAX_TRADE_FEED_ROWS = 50;

/** Live chart canvas pixel height — desktop default and the reduced value applied
 *  below the `sm` breakpoint so the chart doesn't dominate small viewports. */
export const CHART_CANVAS_HEIGHT_DESKTOP = 420;
export const CHART_CANVAS_HEIGHT_MOBILE = 280;

/** Chart area height classes — desktop and mobile. Kept in lockstep with the
 *  live-canvas heights above so the skeleton/empty/error placeholders don't
 *  jump when the live chart mounts (mobile is the exact match; desktop differs
 *  by the pre-existing 400↔420 placeholder offset). */
export const CHART_HEIGHT_CLASS = 'h-[400px] max-sm:h-[280px]';

/** Selectable chart timeframes with display labels. Each value is a lookback
 *  WINDOW (not a bucket); the backend bucket is derived in chart-factory.ts.
 *  The live selector is driven by `ChartControls`' own `INTERVALS` list — this
 *  is the canonical label reference. */
export const INTERVAL_OPTIONS: ReadonlyArray<{ value: ChartInterval; label: string }> = [
  { value: 'live', label: 'Live' },
  { value: '1h', label: '1H' },
  { value: '6h', label: '6H' },
  { value: '1d', label: '1D' },
  { value: '1w', label: '1W' },
  { value: '1mo', label: '1M' },
  { value: 'all', label: 'All' },
] as const;
